// The builder's live count (UI-4). Every edit that leaves a whole definition
// behind is counted — after a pause in the typing, the previous request
// cancelled — by `POST /segments/preview`, which compiles the draft with the
// same function the build and the campaign snapshot use. The panel says how
// many the definition selects, how many of those a send would reach and why
// the rest would not, and shows ten of them with the values they matched on.

import { useEffect, useRef, useState } from "react";
import { CheckIcon, ShieldCheckIcon } from "lucide-react";
import { previewSegment } from "@/lib/api";
import type { PreviewResult, PreviewSample } from "@/lib/api";
import { count, share } from "@/components/emc/format";
import { cn } from "@/lib/utils";
import { messageOf } from "@/pages/segments/data";
import { EngineError } from "@/pages/segments/EngineError";
import { sampleValue } from "@/pages/segments/format";
import { reasonsFor, subjectLabel, subjectOf } from "@/pages/segments/model";
import type { Assembled, BuilderDraft, Names } from "@/pages/segments/model";

const DEBOUNCE_MS = 600;
const SAMPLE = 10;
const VALUE_COLUMNS = 3;

export interface LiveState {
  status: "idle" | "counting" | "done" | "error";
  /** The last answer — kept on screen, dimmed, while the next one is counted. */
  result: PreviewResult | null;
  /** The rows the answer was counted for: its flags point at these. */
  counted: BuilderDraft | null;
  at: Date | null;
  error: string | null;
  /** Why nothing is being counted. */
  waiting: string | null;
}

const IDLE: LiveState = { status: "idle", result: null, counted: null, at: null, error: null, waiting: null };

export function useLiveCount(country: string, assembled: Assembled, draft: BuilderDraft): LiveState {
  const [state, setState] = useState<LiveState>(IDLE);
  const latest = useRef(draft);
  latest.current = draft;
  const key = assembled.ok ? JSON.stringify([country, assembled.definition]) : null;
  const waiting = assembled.ok
    ? null
    : (assembled.general ?? "Egészítsd ki a feltételeket — a számolás magától indul.");

  useEffect(() => {
    if (key === null) {
      setState((current) => ({ ...current, status: "idle", waiting, error: null }));
      return undefined;
    }
    const [forCountry, definition] = JSON.parse(key) as [string, Record<string, unknown>];
    const counted = latest.current;
    const controller = new AbortController();
    // Counting starts now as far as the reader is concerned: the pause only
    // waits for the typing to stop, and the last sentence ("add a condition")
    // is no longer true.
    setState((current) => ({ ...current, status: "counting", waiting: null, error: null }));
    const timer = window.setTimeout(() => {
      previewSegment(forCountry, definition, { sample: SAMPLE, signal: controller.signal })
        .then((result) =>
          setState({ status: "done", result, counted, at: new Date(), error: null, waiting: null }),
        )
        .catch((raised: unknown) => {
          if (controller.signal.aborted) return;
          setState((current) => ({ ...current, status: "error", error: messageOf(raised) }));
        });
    }, DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [key, waiting]);

  return state;
}

function time(at: Date | null): string {
  return at ? at.toLocaleTimeString("hu-HU", { hour: "2-digit", minute: "2-digit", second: "2-digit" }) : "";
}

/** The fields the sample was chosen on, in the order the definition names them. */
function usedFields(samples: PreviewSample[]): string[] {
  const seen: string[] = [];
  for (const sample of samples) {
    for (const field of Object.keys(sample.values)) if (!seen.includes(field)) seen.push(field);
  }
  return seen.slice(0, VALUE_COLUMNS);
}

function fieldLabel(field: string): string {
  const { subject, attrKey } = subjectOf(field);
  return attrKey ? attrKey : subjectLabel(subject);
}

export function LiveCountPanel({
  state,
  names,
  className,
}: {
  state: LiveState;
  names?: Names;
  className?: string;
}) {
  const result = state.result;
  const stale = state.status === "counting" || state.status === "idle";
  const fields = result ? usedFields(result.samples) : [];
  const counted = state.counted;
  // Inside a VAGY group: which alternative this contact came in by.
  const reasons = (sample: PreviewSample) => (counted ? reasonsFor(counted, sample.matched, names) : []);

  const leftOutForConsent = result ? result.count - result.subscribed : 0;
  const leftOutSuppressed = result ? result.subscribed - result.mailable : 0;
  // The permanent list-hygiene rules the snapshot runs by default (2.4).
  // The Klaviyo handover (0028) comes off after the hygiene rules.
  const leftOutHandover = result?.handover ?? 0;
  const leftOutHygiene = result ? result.mailable - result.sendable - leftOutHandover : 0;

  return (
    <section
      aria-label="Élő darabszám"
      aria-busy={state.status === "counting"}
      className={cn("flex flex-col gap-4 rounded-card border border-line bg-surface p-4 shadow-emc", className)}
    >
      <div className="flex items-baseline justify-between gap-3">
        <h2 className="caps-label">Élő darabszám</h2>
        <span className="text-label text-muted-foreground" role="status">
          {state.status === "counting"
            ? "számolás…"
            : state.status === "done"
              ? `számolva ${time(state.at)}`
              : state.status === "error"
                ? "a számolás nem sikerült"
                : ""}
        </span>
      </div>

      {state.waiting && <p className="text-muted-foreground">{state.waiting}</p>}
      {state.status === "error" && state.error && <EngineError message={state.error} names={names?.segments} />}

      {result && (
        <div className={cn("flex flex-col gap-4", stale && "opacity-60")}>
          <div className="flex flex-col gap-0.5">
            <span className="text-number leading-tight font-semibold text-ink tabular-nums">{count(result.count)}</span>
            <span className="text-muted-foreground">kontakt illik a feltételekre ebben az országban</span>
          </div>

          <div className="flex flex-col gap-2 rounded-control border border-line bg-surface-2 p-3">
            <div className="flex flex-wrap items-baseline justify-between gap-2">
              <span className="font-medium text-ink">A küldéskor ennyi marad</span>
              <span className="text-section font-semibold text-blue-ink tabular-nums">{count(result.sendable)}</span>
            </div>
            <span className="text-label text-muted-foreground">
              {share(result.sendable, result.count) ? `a találatok ${share(result.sendable, result.count)}-a` : "nincs találat"}
            </span>
            <dl className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-4 gap-y-1 text-label">
              <dt className="text-muted-foreground">nem feliratkozott (leiratkozott, soha nem iratkozott fel, megerősítésre vár)</dt>
              <dd className="m-0 text-right text-ink tabular-nums">− {count(leftOutForConsent)}</dd>
              <dt className="text-muted-foreground">kizárt cím (leiratkozás, visszapattanás, panasz…)</dt>
              <dd className="m-0 text-right text-ink tabular-nums">− {count(leftOutSuppressed)}</dd>
              <dt className="text-muted-foreground">hibás, eldobható vagy csapdagyanús cím (a listatisztítás kizárja)</dt>
              <dd className="m-0 text-right text-ink tabular-nums">− {count(leftOutHygiene)}</dd>
              {result.handover_wave != null && (
                <>
                  <dt className="text-muted-foreground">
                    a Klaviyóé a migráció alatt (hullám &gt; {result.handover_wave} vagy nincs szinkronizálva)
                  </dt>
                  <dd className="m-0 text-right text-ink tabular-nums">− {count(leftOutHandover)}</dd>
                </>
              )}
            </dl>
          </div>

          <p className="flex items-start gap-2 text-label text-muted-foreground">
            <ShieldCheckIcon className="mt-0.5 size-3.5 shrink-0 text-blue-ink" />
            <span>
              A leiratkozottakat, a kizárt és a hibás címeket a kampány a küldés pillanatában mindig kiveszi — a szegmens
              ezt nem tudja megkerülni. A gyakorisági korlát, a kampány kizáró szegmensei és a bekapcsolható
              listatisztítás (szerepcím, régóta inaktív) a kampányon múlnak, ezért itt nem látszanak.
            </span>
          </p>

          {result.samples.length > 0 ? (
            <div className="flex flex-col gap-2">
              <h3 className="caps-label">Minta: az első {result.samples.length} illeszkedő kontakt</h3>
              <ul aria-label="Minta a találatokból" className="flex flex-col divide-y divide-line rounded-control border border-line">
                {result.samples.map((sample) => {
                  const why = reasons(sample);
                  return (
                    <li key={sample.id} className="flex min-w-0 flex-col gap-0.5 px-3 py-2">
                      <span className="data-mono truncate text-ink">{sample.email}</span>
                      {fields.length > 0 && (
                        <span className="text-label text-muted-foreground">
                          {fields
                            .map((field) => `${fieldLabel(field)}: ${sampleValue(subjectOf(field).subject, sample.values[field])}`)
                            .join(" · ")}
                        </span>
                      )}
                      {why.length > 0 && (
                        <span className="flex items-start gap-1 text-label text-green-ink">
                          <CheckIcon className="mt-0.5 size-3 shrink-0" />
                          <span>{why.join(" · ")}</span>
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ) : (
            <p className="text-muted-foreground">Ezekre a feltételekre most senki nem illik.</p>
          )}
        </div>
      )}
    </section>
  );
}
